import React from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { Search, MapPin, CreditCard, ArrowRight } from 'lucide-react';

const HowItWorks = [    
  {
    id: 1,
    icon: <Search className="w-12 h-12 text-[#1976D2]" />, 
    title: "Choose a skill & tutor",
    description: "Browse skills, compare tutors, and select the option that fits your learning goals.",
    color: "border-blue-100",
    glow: "group-hover:shadow-blue-200/50"
  },
  {
    id: 2,
    icon: <MapPin className="w-12 h-12 text-[#1976D2]" />,
    title: "Learn Online or at a Cohub",
    description: "Join one-on-one or group classes, either online or in shared learning spaces.",
    color: "border-orange-100",
    glow: "group-hover:shadow-orange-200/50"
  },
  {
    id: 3,
    icon: <CreditCard className="w-12 h-12 text-[#1976D2]" />,
    title: "Track Progress & Pay Flexibly",
    description: "Monitor your learning journey and pay for classes in a way that works for you.",
    color: "border-purple-100",
    glow: "group-hover:shadow-purple-200/50"
  },
];

const WorkCard = ({ item, index }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const mouseXSpring = useSpring(x);
  const mouseYSpring = useSpring(y);

  // Smooth rotation values
  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["15deg", "-15deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-15deg", "15deg"]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: index * 0.2, ease: "easeOut" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      className={`relative bg-white rounded-[48px] p-12 border-2 ${item.color} shadow-xl ${item.glow} transition-shadow duration-500 flex flex-col items-center text-center group cursor-pointer`}
    >
      {/* 3D Floating Step Badge */}
      <div 
        style={{ transform: "translateZ(50px)" }}
        className="absolute -top-7 left-1/2 -translate-x-1/2 w-14 h-14 bg-slate-900 text-white rounded-full flex items-center justify-center font-black text-xl shadow-2xl border-4 border-white group-hover:bg-[#1976D2] group-hover:scale-110 transition-all duration-300"
      >
        {item.id}
      </div>

      {/* Icon with Depth */}
      <div style={{ transform: "translateZ(80px)" }} className="mb-10 mt-4">
        <div className="p-6 bg-slate-50 rounded-3xl group-hover:bg-[#1976D2]/5 transition-colors duration-500">
          {item.icon}
        </div>
      </div>

      {/* Text Content with Depth */}
      <div style={{ transform: "translateZ(40px)" }}>
        <h2 className="text-2xl font-black mb-5 text-slate-800 tracking-tight">
          {item.title}
        </h2>
        <p className="text-slate-500 leading-relaxed font-medium px-2">
          {item.description}
        </p>
      </div>

      {/* Decorative Internal Glow */}
      <div className="absolute inset-0 rounded-[48px] bg-gradient-to-br from-white via-transparent to-slate-50/50 pointer-events-none" />
    </motion.div>
  );
};

const HowItWorksSection = () => {
  return (
    <section className="w-full px-6 sm:px-12 lg:px-24 py-32 bg-[#FCFAF7] overflow-hidden perspective-[1500px]">
      {/* Dynamic Header */}
      <div className="max-w-4xl mx-auto text-center mb-28 relative">
               <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-5xl md:text-7xl font-black text-slate-900 mb-8 tracking-tighter"
        >
          How Peer2learn <span className="text-[#1976D2] relative">
            Works
            <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 100 10" fill="none">
              <path d="M1 9C30 2 70 2 99 9" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
            </svg>
          </span>
        </motion.h1>
      </div>

      {/* Connector Path and Cards */}
      <div className="max-w-7xl mx-auto relative">
        {/* The "Mission" Style Dotted Path */}
        <svg className="hidden lg:block absolute top-1/2 left-0 w-full h-1 pointer-events-none -translate-y-1/2 opacity-10" style={{ zIndex: 0 }}>
          <line x1="15%" y1="0" x2="85%" y2="0" stroke="black" strokeWidth="4" strokeDasharray="12 20" strokeLinecap="round" />
        </svg>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-14 relative z-10">
          {HowItWorks.map((item, index) => (
            <WorkCard key={item.id} item={item} index={index} />
          ))}
        </div>
      </div>

      {/* Final Call to Action */}
      <motion.div 
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center mt-32"
      >
      
        
       
      </motion.div>
    </section>
  );
};

export default HowItWorksSection;